import React, { useState } from "react";
import { Box, Divider } from "@mui/material";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import TermsConditionsModal from "@/main/account/voucherwallet/_components/terms-conditions-modal";
import VoucherInformationModal from "@/main/account/voucherwallet/_components/voucher-information-modal";
import { useMutation } from "@tanstack/react-query";
import { useAlertStore } from "@/components/context/alert-store";
import BarcodeModal from "@/main/account/voucherwallet/_components/barcode-modal";
import VoucherInfo from "@/main/account/voucherwallet/_components/voucher-info";
import MoreOptions from "@/main/account/voucherwallet/_components/more-options";
import VoucherActions from "@/main/account/voucherwallet/_components/voucher-actions";
import { IVoucherItemProps } from "@/types/voucherwallet";
import { getRequest } from "@/utils/request";

const VoucherItem: React.FC<IVoucherItemProps> = ({ voucherItem, lang, isMoreThanOne }) => {
  const { i18n } = useLingui();
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);
  const [modalTermsOpen, setModalTermsOpen] = useState(false);
  const [modalBarcodeOpen, setModalBarcodeOpen] = useState(false);
  const [modalInformationOpen, setModalInformationOpen] = useState(false);
  const { setMessage } = useAlertStore();
  const updateMutation = useMutation({
    mutationFn: (uuid: string) =>
      getRequest({ path: { name: "markRedeemed", id: `${uuid}` }, lang, skipCache: true }),
  });
  const handleOnClickRedeemed = async () => {
    updateMutation.mutate(voucherItem.uuid, {
      onSuccess: () => {
        voucherItem.redeem_status = "1";
        setMessage({
          text: t(i18n)`Voucher is marked as redeemed!`,
          severity: "success",
        });
      },
      onError: () => {
        setMessage({
          text: t(i18n)`An unexpected error occurred. Please try again later.`,
          severity: "error",
        });
      },
    });
  };

  const handleOnClickDownload = async () => {
    if (voucherItem.pdf_link && voucherItem.pdf_link !== "" && voucherItem.pdf_link !== null) {
      window.open(voucherItem.pdf_link, "_blank");
    } else {
      window.open(
        `${process.env.NEXT_PUBLIC_WEB_URL}/voucher_render_pdf/${voucherItem.uuid}/${lang}`,
        "_blank",
      );
    }
  };
  const handleMoreOptionsClick = () => setMoreOptionsOpen(!moreOptionsOpen);
  const handleModalTermsOpen = () => setModalTermsOpen(true);
  const handleModalTermsClose = () => setModalTermsOpen(false);
  const handleModalInformationOpen = () => setModalInformationOpen(true);
  const handleModalInformationClose = () => setModalInformationOpen(false);
  const handleModalBarcodeOpen = () => setModalBarcodeOpen(true);
  const handleModalBarcodeClose = () => setModalBarcodeOpen(false);

  return (
    <Box
      sx={{
        border: "1px solid #eee",
        borderRadius: "10px",
        padding: "16px",
        boxShadow: "0 2px 8px 0 rgba(40, 43, 47, 0.25)",
        maxWidth: isMoreThanOne ? "450px" : "100%",
        width: "100%",
      }}
    >
      <VoucherInfo voucherItem={voucherItem} handleModalBarcodeOpen={handleModalBarcodeOpen} />
      <Divider sx={{ mt: "10px" }} />
      <VoucherActions
        voucherItem={voucherItem}
        handleOnClickDownload={handleOnClickDownload}
        handleModalTermsOpen={handleModalTermsOpen}
      />
      <Divider />
      <MoreOptions
        moreOptionsOpen={moreOptionsOpen}
        handleMoreOptionsClick={handleMoreOptionsClick}
        handleModalInformationOpen={handleModalInformationOpen}
        handleOnClickRedeemed={handleOnClickRedeemed}
        redeemStatus={voucherItem.redeem_status ?? ""}
      />
      {voucherItem.jit_terms_and_cond_popup && (
        <TermsConditionsModal
          open={modalTermsOpen}
          onClose={handleModalTermsClose}
          termsContent={voucherItem.jit_terms_and_cond_popup.content}
        />
      )}
      <VoucherInformationModal
        open={modalInformationOpen}
        onClose={handleModalInformationClose}
        uuid={voucherItem.uuid}
        lang={lang}
      />
      {voucherItem.barcode_popup_url !== "" && (
        <BarcodeModal
          open={modalBarcodeOpen}
          onClose={handleModalBarcodeClose}
          uuid={voucherItem.uuid}
          orderId={voucherItem.order_id}
          productImage={voucherItem.jit_product_image}
          lang={lang}
        />
      )}
    </Box>
  );
};
export default VoucherItem;
